import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  Overlay,
  ScrollStrategy
} from '@angular/cdk/overlay';
import {
  Component,
  ContentChild,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { TooltipManagerService } from '@services';
import { Subscription } from 'rxjs';

export type SmartTooltipPlacement = 'left' | 'right' | 'top' | 'bottom';

// Global delay constant - uniform across the entire application
const TOOLTIP_SHOW_DELAY_MS = 500;
const TOOLTIP_HIDE_DELAY_MS = 150;

@Component({
  selector: 'cds-tooltip',
  templateUrl: './cds-tooltip.component.html',
  styleUrls: ['./cds-tooltip.component.scss']
})
export class CdsTooltipComponent implements OnInit, OnChanges, OnDestroy {
  @Input() origin!: CdkOverlayOrigin;
  @Input() tooltipId?: string; // Unique ID for this tooltip instance

  private _open = false;
  private _hover = false; // Track hover state separately from open state

  @Input()
  set open(value: boolean) {
    if (value === this._open) return;
    this._open = value;

    if (value) {
      this.showOverlayWithDelay();
    } else {
      this.hideOverlayWithDelay();
    }
  }

  get open(): boolean {
    return this._open;
  }

  // Allow manual control of hover behavior
  @Input() enableHoverDelay = true;
  @Input() showDelay = TOOLTIP_SHOW_DELAY_MS;
  @Input() hideDelay = TOOLTIP_HIDE_DELAY_MS;

  @Input() placement: SmartTooltipPlacement = 'left';
  @Input() panelWidth: number | 'auto' = 200;
  @Input() hasBackdrop = false;
  @Input() closeOnOutsideClick = false;

  @Output() hovered = new EventEmitter<void>();
  @Output() unhovered = new EventEmitter<void>();
  @Output() openChange = new EventEmitter<boolean>();

  @ContentChild('tooltipContent', { static: false })
  tooltipContentTpl!: TemplateRef<unknown>;

  @ViewChild(CdkConnectedOverlay)
  connectedOverlay?: CdkConnectedOverlay;

  arrowOffset: number | null = null;
  arrowDirection: SmartTooltipPlacement = 'right';

  positions: ConnectedPosition[] = [];
  private overlay = inject(Overlay);
  private tooltipManager = inject(TooltipManagerService);
  scrollStrategy: ScrollStrategy;

  internalOpen = false;
  isVisible = false;
  private hideTimeoutId: any = null;
  private showTimeoutId: any = null;
  private subscriptions = new Subscription();

  private readonly globalScrollHandler = () => {
    if (this.internalOpen && this.connectedOverlay?.overlayRef) {
      this.connectedOverlay.overlayRef.updatePosition();
    }
  };

  constructor() {
    this.positions = this.buildPositions(this.placement);
    this.scrollStrategy = this.overlay.scrollStrategies.reposition();
    window.addEventListener('scroll', this.globalScrollHandler, true);
  }

  ngOnInit(): void {
    // Generate a unique ID if not provided
    if (!this.tooltipId) {
      this.tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;
    }

    // Subscribe to global tooltip changes to close this tooltip if another opens
    this.subscriptions.add(
      this.tooltipManager.currentTooltipId.subscribe((activeId) => {
        if (activeId !== null && activeId !== this.tooltipId && this.internalOpen) {
          this.forceClose();
        }
      })
    );

    // Subscribe to close all tooltips event
    this.subscriptions.add(
      this.tooltipManager.closeAllTooltips.subscribe(() => {
        this.forceClose();
      })
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.globalScrollHandler, true);
    this.clearAllTimeouts();
    this.subscriptions.unsubscribe();

    // Unregister this tooltip when component is destroyed
    if (this.tooltipId) {
      this.tooltipManager.unregisterTooltip(this.tooltipId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['placement']) {
      this.positions = this.buildPositions(this.placement);
    }
  }

  get overlayOffsetX(): number {
    if (this.placement === 'left') return -13;
    if (this.placement === 'right') return 13;
    return 0;
  }

  get overlayOffsetY(): number {
    if (this.placement === 'top') return -13;
    if (this.placement === 'bottom') return 13;
    return 0;
  }

  private buildPositions(placement: SmartTooltipPlacement): ConnectedPosition[] {
    switch (placement) {
      case 'right':
        return [
          // Primary: right positions
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
          { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
          // Fallback: left positions
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' },
          { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
          { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' }
        ];
      case 'top':
        return [
          // Primary: top positions
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
          // Fallback: right positions
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
          { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' }
        ];
      case 'bottom':
        return [
          // Primary: bottom positions
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
          // Fallback: right positions
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
          { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' }
        ];
      case 'left':
      default:
        return [
          // Primary: left positions
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' },
          { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
          { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' },
          // Fallback: right positions
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
          { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' }
        ];
    }
  }

  /**
   * Clear all pending timeouts
   */
  private clearAllTimeouts(): void {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
  }

  /**
   * Force close the tooltip immediately without delay
   */
  private forceClose(): void {
    this.clearAllTimeouts();
    this._open = false;
    this.isVisible = false;
    this.internalOpen = false;
    if (this.tooltipId) {
      this.tooltipManager.unregisterTooltip(this.tooltipId);
    }
    // Emit openChange event to notify parent component
    this.openChange.emit(false);
  }

  /**
   * Show overlay with configurable delay
   */
  private showOverlayWithDelay(): void {
    this.clearAllTimeouts();

    const delayToUse = this.enableHoverDelay ? this.showDelay : 0;

    this.showTimeoutId = setTimeout(() => {
      // Register this tooltip as active
      if (this.tooltipId) {
        this.tooltipManager.registerTooltip(this.tooltipId);
      }

      this.internalOpen = true;
      this.isVisible = false;

      setTimeout(() => {
        if (this.internalOpen) {
          this.isVisible = true;
        }
      }, 0);

      this.showTimeoutId = null;
    }, delayToUse);
  }

  /**
   * Hide overlay with configurable delay
   */
  private hideOverlayWithDelay(): void {
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }

    if (!this.internalOpen) return;

    this.isVisible = false;

    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
    }

    this.hideTimeoutId = setTimeout(() => {
      this.internalOpen = false;
      this.hideTimeoutId = null;

      // Unregister this tooltip when it closes
      if (this.tooltipId) {
        this.tooltipManager.unregisterTooltip(this.tooltipId);
      }
    }, this.hideDelay);
  }

  onPositionChange(event: ConnectedOverlayPositionChange): void {
    if (!this.connectedOverlay || !this.origin) return;

    const pair = event.connectionPair;

    if (pair.overlayX === 'end' && pair.originX === 'start') {
      this.arrowDirection = 'right';
    } else if (pair.overlayX === 'start' && pair.originX === 'end') {
      this.arrowDirection = 'left';
    } else if (pair.overlayY === 'bottom' && pair.originY === 'top') {
      this.arrowDirection = 'bottom';
    } else if (pair.overlayY === 'top' && pair.originY === 'bottom') {
      this.arrowDirection = 'top';
    }

    const overlayRef = this.connectedOverlay.overlayRef;
    if (!overlayRef) return;

    const overlayEl = overlayRef.overlayElement;
    const overlayRect = overlayEl.getBoundingClientRect();

    const originEl = this.origin.elementRef.nativeElement as HTMLElement;
    const originRect = originEl.getBoundingClientRect();

    const padding = 16;

    if (this.arrowDirection === 'left' || this.arrowDirection === 'right') {
      const originCenterY = originRect.top + originRect.height / 2;
      const offsetInsideOverlay = originCenterY - overlayRect.top;
      const clamped = Math.max(
        padding,
        Math.min(overlayRect.height - padding, offsetInsideOverlay)
      );
      this.arrowOffset = clamped;
    } else {
      const originCenterX = originRect.left + originRect.width / 2;
      const offsetInsideOverlay = originCenterX - overlayRect.left;
      const clamped = Math.max(padding, Math.min(overlayRect.width - padding, offsetInsideOverlay));
      this.arrowOffset = clamped;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open || !this.closeOnOutsideClick) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest('.countries-tooltip')) {
      return;
    }

    const originEl = this.origin?.elementRef.nativeElement as HTMLElement | null;
    const overlayEl = this.connectedOverlay?.overlayRef?.overlayElement as HTMLElement | null;

    const clickInOrigin = !!originEl && originEl.contains(target);
    const clickInOverlay = !!overlayEl && overlayEl.contains(target);

    if (!clickInOrigin && !clickInOverlay) {
      this.openChange.emit(false);
    }
  }
}
